import { useEffect, useRef, useState } from 'react';

import { DatePicker, Empty, Skeleton, message } from 'antd';
import moment from 'moment';
import { T } from 'react-translator-component';
import { useTheme } from 'styled-components';

import {
  DASH,
  actorLabel,
  changeLabel,
  metadataEntries,
  targetLabel,
  valueCell,
} from './listFormater';
import {
  Breadcrumb,
  Container,
  EmptyWrap,
  FilterCard,
  PageHead,
  SummaryCard,
  DetailButton,
  DetailModal,
  MetadataList,
  SummaryRow,
  TableCard,
} from './styled';

import { getAuditLog, getAuditLogTypes } from '../../../hooks/auditLog';
import AuditIcon from '../../atoms/icons/AuditIcon';
import { ListSkeleton } from '../../molecules/ListSkeleton/ListSkeleton';
import { Pagination } from '../../molecules/Pagination/Pagination';
import { Select } from '../../organisms/Select';

const { RangePicker } = DatePicker;
const PAGE_SIZE = 10;
const MAX_RANGE_DAYS = 90;

const defaultRange = (): [any, any] => [moment().subtract(7, 'days'), moment()];
const dayStart = (m: any) => m.clone().startOf('day').format('YYYY-MM-DD HH:mm:ss');
const dayEnd = (m: any) => m.clone().endOf('day').format('YYYY-MM-DD HH:mm:ss');

const AuditLogList = () => {
  const colorTheme: any = useTheme();
  const [list, setList] = useState<any[]>([]);
  const [count, setCount] = useState<number>(0);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [typeOptions, setTypeOptions] = useState<any[]>([]);
  const [detailRow, setDetailRow] = useState<any>(null);
  const [summaryLoading, setSummaryLoading] = useState<boolean>(true);
  const [loadFailed, setLoadFailed] = useState<boolean>(false);

  const [typeInput, setTypeInput] = useState<string>('');
  const [pickerValue, setPickerValue] = useState<any>(defaultRange);
  const [pickerDates, setPickerDates] = useState<any>(null);

  const [actionType, setActionType] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>(() => dayStart(defaultRange()[0]));
  const [toDate, setToDate] = useState<string>(() => dayEnd(defaultRange()[1]));

  const filterKey = `${actionType}|${fromDate}|${toDate}`;
  const lastFilterKey = useRef<string | null>(null);

  const fetchData = async () => {
    const filtersChanged = lastFilterKey.current !== filterKey;
    lastFilterKey.current = filterKey;

    setLoading(true);
    if (filtersChanged) {
      setSummaryLoading(true);
    }
    try {
      const res = await getAuditLog({
        actionType,
        fromDate,
        toDate,
        page: currentPage,
        size: PAGE_SIZE,
      });

      if (!res || res.type === 'error' || !Array.isArray(res.results)) {
        setLoadFailed(true);
        setList([]);
        setCount(0);
        setSummary(null);
        message.error(T('Could not load the audit records'));
        return;
      }

      setLoadFailed(false);
      setList(res.results);
      setCount(res.count || 0);
      setSummary(res.summary || null);
    } finally {
      setLoading(false);
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, actionType, fromDate, toDate]);

  useEffect(() => {
    (async () => {
      const types = await getAuditLogTypes();
      if (!Array.isArray(types)) {
        message.error(T('Could not load the change types'));
      }
      const options = Array.isArray(types) ? types : [];
      setTypeOptions([
        { value: '', id: 'audit-type-all', label: T('All types') },
        ...options.map((t: any) => ({
          value: t.value,
          id: t.value,
          label: changeLabel(t.value, t.label),
        })),
      ]);
    })();
  }, []);

  const disabledDate = (current: any) => {
    if (!current) return false;
    if (current > moment().endOf('day')) return true;
    if (!pickerDates || pickerDates.length === 0) return false;
    const start = pickerDates[0];
    const end = pickerDates[1];
    const tooLate = start && current.diff(start, 'days') >= MAX_RANGE_DAYS;
    const tooEarly = end && end.diff(current, 'days') >= MAX_RANGE_DAYS;
    return !!tooLate || !!tooEarly;
  };

  const onRangeChange = (values: any) => {
    setPickerValue(values && values[0] && values[1] ? values : defaultRange());
  };

  const onFilter = () => {
    setActionType(typeInput);
    setFromDate(dayStart(pickerValue[0]));
    setToDate(dayEnd(pickerValue[1]));
    setCurrentPage(1);
  };

  const onClear = () => {
    const def = defaultRange();
    setTypeInput('');
    setPickerValue(def);
    setPickerDates(null);
    setActionType('');
    setFromDate(dayStart(def[0]));
    setToDate(dayEnd(def[1]));
    setCurrentPage(1);
  };

  const from = count === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const to = Math.min(currentPage * PAGE_SIZE, count);

  return (
    <Container>
      <PageHead>
        <AuditIcon fill={colorTheme.primary} />
        <span className='page-title'>{T('Audit Log')}</span>
      </PageHead>
      <Breadcrumb>
        <b>{T('Change history in the system')}</b>
      </Breadcrumb>

      <FilterCard>
        <div className='field type-field'>
          <label>{T('Change type')}</label>
          <Select value={typeInput} options={typeOptions} action={setTypeInput} />
        </div>
        <div className='field'>
          <label>{T('Period')}</label>
          <RangePicker
            format='DD/MM/YYYY'
            allowClear
            value={pickerValue}
            disabledDate={disabledDate}
            onOpenChange={(open: boolean) => {
              if (open) setPickerDates([null, null]);
            }}
            onCalendarChange={(vals: any) => setPickerDates(vals)}
            onChange={onRangeChange}
          />
        </div>
        <div className='actions'>
          <button type='button' onClick={onClear}>
            {T('Clear')}
          </button>
          <button type='button' className='primary' onClick={onFilter}>
            {T('Filter')}
          </button>
        </div>
      </FilterCard>

      <SummaryRow>
        <SummaryCard>
          <span className='label'>{T('Changes in period')}</span>
          {summaryLoading ? (
            <Skeleton active paragraph={false} title={{ width: 90 }} />
          ) : (
            <span className='value'>
              {loadFailed ? DASH : summary?.changesInPeriod ?? 0}
            </span>
          )}
          <span className='sub'>{T('Total records')}</span>
        </SummaryCard>
        <SummaryCard>
          <span className='label'>{T('Last change')}</span>
          {summaryLoading ? (
            <Skeleton active paragraph={false} title={{ width: 170 }} />
          ) : (
            <span className='value gray'>
              {changeLabel(
                summary?.lastChange?.actionType,
                summary?.lastChange?.changeDescription,
              )}
            </span>
          )}
          <span className='sub'>
            {!summaryLoading && summary?.lastChange?.createdTimestamp
              ? moment(summary.lastChange.createdTimestamp).format('DD/MM/YYYY HH:mm')
              : ''}
          </span>
        </SummaryCard>
      </SummaryRow>

      <TableCard>
        <div className='table-card-head'>
          <h3>
            {T('Audit records')}
            {!summaryLoading && !loadFailed && (
              <span className='count'>
                {count} {T('results')}
              </span>
            )}
          </h3>
        </div>

        {loading ? (
          <ListSkeleton style={{ padding: 20 }} />
        ) : list.length === 0 ? (
          <EmptyWrap>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                loadFailed
                  ? T('Could not load the audit records')
                  : T('No records found')
              }
            />
          </EmptyWrap>
        ) : (
          <>
            <div className='table-scroll'>
              <table>
                <thead>
                  <tr>
                    <th>{T('What changed')}</th>
                    <th>{T('Where')}</th>
                    <th>{T('Changed by')}</th>
                    <th>{T('Previous value')}</th>
                    <th>{T('New value')}</th>
                    <th>{T('Date/Time')}</th>
                    <th aria-label={T('Details')} />
                  </tr>
                </thead>
                <tbody>
                  {list.map((row: any, index: number) => (
                    <tr key={index}>
                      <td>
                        <div className='change-name'>
                          {changeLabel(row.actionType, row.changeDescription)}
                        </div>
                        {row.tenantName && (
                          <div className='change-tenant'>{row.tenantName}</div>
                        )}
                      </td>
                      <td className='target-label'>{targetLabel(row)}</td>
                      <td>{actorLabel(row)}</td>
                      <td className='value-old'>{valueCell(row.oldValue)}</td>
                      <td className='value-new'>{valueCell(row.newValue)}</td>
                      <td className='timestamp'>
                        {row.createdTimestamp
                          ? moment(row.createdTimestamp).format('DD/MM/YYYY HH:mm')
                          : DASH}
                      </td>
                      <td>
                        {row.metadata ? (
                          <DetailButton
                            type='button'
                            onClick={() => setDetailRow(row)}
                          >
                            {T('View details')}
                          </DetailButton>
                        ) : (
                          <span className='value-dash'>{DASH}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className='table-footer'>
              <div>
                {T('Showing')} {from}-{to} {T('of')} {count}
              </div>
              <Pagination
                currentPage={currentPage}
                totalCount={count}
                pageSize={PAGE_SIZE}
                onPageChange={(page: number) => setCurrentPage(page)}
              />
            </div>
          </>
        )}
      </TableCard>

      <DetailModal
        centered
        visible={!!detailRow}
        title={T('Record details')}
        footer={null}
        onCancel={() => setDetailRow(null)}
        width='fit-content'
      >
        <MetadataList>
          {metadataEntries(detailRow?.metadata).map((entry, i) => (
            <div className='entry' key={i}>
              {entry.label && <span className='entry-label'>{entry.label}</span>}
              <span className='entry-value'>{entry.value || DASH}</span>
            </div>
          ))}
        </MetadataList>
      </DetailModal>
    </Container>
  );
};

export default AuditLogList;
